import * as React from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import type { CustomField, CustomSection, DocumentUpload, OptionPoints } from '@gcv/shared';
import { CustomFieldDataTypeOptions, CustomFieldType, CustomFieldTypeOptions } from '@gcv/shared';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { ReactComponent as LockIcon } from 'assets/images/ic-lock-dark.svg';
import { environment } from 'environments/environment';
import {
  Button,
  Container,
  ContainerItem,
  Dialog,
  Error,
  FileContainer,
  Form,
  InputCheckBox,
  InputRadio,
  InputSelect,
  InputText,
  InputTextArea,
  InputUrl,
  Text
} from 'ui';
import { theme } from 'ui/theme';
import palette from 'ui/theme/palette';
import { setDefaultValues } from 'util/forms.util';
import { parseFieldOptions, syncOptionPoints } from './option-points.util';
import { WeightInput } from './weight-input';

interface Props {
  field?: CustomField;
  formStatus: 'draft' | 'published';
  isOpen: boolean;
  onSave: (data: FieldValues, isCreatingAnotherField: boolean, isCreatingSmartRule: boolean) => void;
  section?: string;
  sectionOptions:
    | {
        label: string;
        value: string;
      }[]
    | undefined;
  onSectionSelect: (value: string) => void;
  setIsOpen: (isOpen: boolean, smartRules: boolean) => void;
  isLoading?: boolean;
  formFields?: CustomField[];
  optionsLocked: { isTarget: boolean; smartRuleSubjects: (CustomField | CustomSection)[] };
  uploadDocuments: (files: File[]) => Promise<DocumentUpload[] | undefined>;
  fiDocuments?: DocumentUpload[] | undefined;
  scored?: boolean;
  maxOptionPoints?: number;
}

export const FieldModal: React.FC<Props> = (props) => {
  // this feature flag is used to enable/disable the File Download field type
  // unlike other flags this should be semi permanent so this is only enabled for gcv internal users
  const { customFieldsFileDownload }: { customFieldsFileDownload: boolean } = useFlags();
  const [activeSection, setActiveSection] = React.useState('');
  const [tempFiles, setTempFiles] = React.useState<File[]>([]);
  const [existingFiles, setExistingFiles] = React.useState<DocumentUpload[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  // to reset the FileContainer when checked Create Another Field check box as data needs to be cleared
  const [refreshFileComponent, setRefreshFileComponent] = React.useState(false);

  // Points are keyed by option label, which React Hook Form would read as a field path, so they're
  // merged in on submit rather than registered as form fields.
  const [optionPoints, setOptionPoints] = React.useState<OptionPoints>(
    (props.field as { option_points?: OptionPoints })?.option_points ?? {}
  );

  const form = useForm({
    mode: 'onBlur',
    defaultValues: setDefaultValues({
      type: props.field?.type ?? '',
      label: props.field?.label ?? '',
      helpText: props.field?.helpText ?? '',
      required: props.field?.required ?? false,
      showHelpText: !!props.field?.helpText,
      section: (props.section || props.field?.section) ?? '',
      createAnotherField: false,
      addSmartRules: false,
      paragraph: props.field?.paragraph ?? '',
      attachments: props.field?.attachments ?? [],
      ...props.field,
      // An existing field is scored if it already carries points; new ones default to scored.
      scoreQuestion: props.field
        ? Object.keys((props.field as { option_points?: OptionPoints }).option_points ?? {}).length > 0
        : true
    })
  });

  // Only radio/select carry points, and only when the consumer opted into scoring.
  const isScoredFieldType =
    !!props.scored &&
    (form.watch('type') === CustomFieldType.Radio || form.watch('type') === CustomFieldType.Select);
  const isQuestionScored = isScoredFieldType && form.watch('scoreQuestion') !== false;
  const scoredOptions = isQuestionScored ? parseFieldOptions(form.watch('options') as string) : [];
  const isOverMaxValue = (option: string) =>
    props.maxOptionPoints !== undefined && (optionPoints[option] ?? 0) > props.maxOptionPoints;
  const hasPointsOverMax = scoredOptions.some(isOverMaxValue);

  const checkIfFieldCanBeSmartRule = (field: CustomField) =>
    field.type === CustomFieldType.Radio || field.type === CustomFieldType.Select;
  const availableSmartRuleFields = props.formFields
    ? props.formFields.filter(checkIfFieldCanBeSmartRule)
    : [];

  React.useEffect(() => {
    setActiveSection(props.section ?? '');

    if (props.section) {
      form.setValue('section', props.section);
      form.trigger();
    }
  }, [props.section]);

  // case: Display Content - set existing files by comparing the docIds in field && fiDocuments
  React.useEffect(() => {
    if (
      props.field?.type === CustomFieldType.DisplayContent ||
      props.field?.type === CustomFieldType.File ||
      props.field?.type === CustomFieldType.FileDownload
    ) {
      const docIds = props.field?.attachments?.map((file) => {
        return file.id;
      });

      const existingFiles = props.fiDocuments?.filter((doc) => docIds?.includes(doc.id) && !doc.deleted);
      setExistingFiles(existingFiles ?? []);
    }
  }, [props.field]);

  const onRemoveExistingFile = (file: DocumentUpload) => {
    if (existingFiles && existingFiles.length) {
      existingFiles.splice(
        existingFiles.findIndex((a) => a.id === file.id),
        1
      );
    }

    setExistingFiles(existingFiles);
  };

  const triggerRefresh = (value: boolean) => {
    setRefreshFileComponent(value);
  };

  return (
    <Dialog
      title={props.field ? 'Edit Field' : 'Add New Field'}
      isOpen={props.isOpen}
      handleClose={() => {
        form.reset({});
        props.setIsOpen(false, false);
      }}
      supportingActions={
        !props.field ? (
          <InputCheckBox
            label="Create another field"
            name="createAnotherField"
            defaultValue={false}
            readOnly={form.watch('addSmartRules')}
            {...form}
          />
        ) : undefined
      }
      action={
        <Button
          label={form.watch('addSmartRules') === true ? 'Next' : props.field ? 'Save' : 'Add Field'}
          color="primary"
          disabled={hasPointsOverMax}
          isLoading={props.isLoading || isLoading}
          onClick={() => {
            form.handleSubmit(async (data) => {
              setIsLoading((value) => !value);
              if (!data.showHelpText) {
                data.helpText = '';
              }

              // newly uploaded files
              let attachments: { id: string; file_name: string }[] = []; // newly uploaded files
              if (tempFiles.length) {
                const uploadedFiles = (await props.uploadDocuments(tempFiles)) || [];
                attachments = uploadedFiles.map((file) => {
                  return {
                    id: file.id,
                    file_name: file.file_name
                  };
                });
              }
              // existing files
              const existingAttachments =
                existingFiles?.map((a) => {
                  return {
                    id: a.id,
                    file_name: a.file_name
                  };
                }) ?? [];
              // merging newly uploaded & existing files
              data.attachments = [...existingAttachments, ...attachments];

              const addSmartRules = data.addSmartRules;

              // ui only prop, do not send to db
              delete data.showHelpText;
              delete data.addSmartRules;

              delete data.scoreQuestion;

              if (isQuestionScored) {
                data.option_points = syncOptionPoints(data.options as string, optionPoints);

                // Assigned rather than deleted, so a cleared weight clears the stored value.
                const weight = data.weight as string | number | undefined;
                const parsedWeight =
                  weight === undefined || weight === null || weight === '' ? undefined : Number(weight);
                data.weight = Number.isFinite(parsedWeight as number) ? parsedWeight : undefined;
              } else {
                // Scoring props must not leak onto unscored field types.
                delete data.option_points;
                delete data.weight;
              }

              setIsLoading((value) => !value);

              if (
                data.type === CustomFieldType.DisplayContent ||
                data.type === CustomFieldType.FileDownload
              ) {
                data.required = false;
              }

              props.onSave(data, data.createAnotherField, addSmartRules);
              if (data.createAnotherField) {
                form.setValue('label', undefined, { shouldValidate: false });
                form.setValue('helpText', undefined, { shouldValidate: false });
                form.setValue('options', undefined, { shouldValidate: false });
                form.setValue('paragraph', '', { shouldValidate: false });

                // to clear files
                setTempFiles([]);
                setExistingFiles([]);
                triggerRefresh(true);
              } else {
                form.reset({});
                props.setIsOpen(false, addSmartRules);
              }
            })();
          }}
          dataCy="save-field-button"
        />
      }
      dataCy="field-modal"
    >
      <Form>
        <Container padding="0" width="100%">
          <ContainerItem padding="0.5rem 0" width="100%">
            <InputSelect
              {...form}
              name="type"
              label="Type"
              options={CustomFieldTypeOptions.filter((option) => {
                if (!customFieldsFileDownload) {
                  return option.value !== CustomFieldType.FileDownload;
                }
                return true;
              })}
              rules={{ required: { value: true, message: 'is required.' } }}
              readOnly={props.field && props.formStatus === 'published'}
              dataCy="field-type"
            />
          </ContainerItem>

          {form.watch('type') !== CustomFieldType.DisplayContent && (
            <ContainerItem padding="0.5rem 0" width="100%">
              <InputText
                {...form}
                name="label"
                label="Label"
                placeholder="Enter field label"
                rules={{ required: { value: true, message: 'is required.' } }}
                dataCy="field-label"
              />
            </ContainerItem>
          )}

          {form.watch('type') === CustomFieldType.Hyperlink && (
            <>
              <ContainerItem padding="0.5rem 0" width="100%">
                <InputUrl
                  {...form}
                  name="url"
                  label="Website url"
                  placeholder="Website url"
                  rules={{ required: { value: true, message: 'is required.' } }}
                  dataCy="field-link-url"
                />
              </ContainerItem>
              <ContainerItem padding="0.5rem 0" width="100%">
                <InputText
                  {...form}
                  name="paragraph"
                  label="Link copy"
                  placeholder="Link copy text"
                  rules={{ required: { value: true, message: 'is required.' } }}
                  dataCy="field-link-copy"
                />
              </ContainerItem>
            </>
          )}

          {form.watch('type') === CustomFieldType.TextSingleLine && (
            <ContainerItem padding="0.5rem 0" width="100%">
              <InputSelect
                {...form}
                name="dataType"
                label="Data Type"
                options={CustomFieldDataTypeOptions}
                rules={{ required: { value: true, message: 'is required.' } }}
                readOnly={props.field && props.formStatus === 'published'}
                dataCy="field-data-type"
              />
            </ContainerItem>
          )}

          {(form.watch('type') === CustomFieldType.Checkbox ||
            form.watch('type') === CustomFieldType.Radio ||
            form.watch('type') === CustomFieldType.Select) && (
            <>
              <ContainerItem padding="0.5rem 0" width="100%">
                <InputText
                  {...form}
                  name="options"
                  label="Options"
                  descriptionText="Enter options separated by ;"
                  placeholder="i.e. First Choice; Second Choice; Third Choice"
                  rules={{
                    validate: (data) => {
                      const value = data as string;

                      if (!value) {
                        return 'is required.';
                      }

                      if (form.watch('type') === CustomFieldType.Checkbox) {
                        return !!value.length || 'invalid. Enter at least one option.';
                      } else {
                        const split = value.split(';');
                        return (
                          (split.length > 1 && split.every((s) => s.length)) ||
                          'invalid. Enter at least two options.'
                        );
                      }
                    },
                    required: { value: true, message: 'is required.' }
                  }}
                  dataCy="field-options"
                  readOnly={props.optionsLocked.isTarget}
                />
              </ContainerItem>
              {isScoredFieldType && (
                <ContainerItem padding="0.5rem 0 0 0" width="100%">
                  <InputRadio
                    {...form}
                    name="scoreQuestion"
                    label="Risk scoring"
                    justifyContent="row"
                    tooltip="A scored question awards points and carries a weight. A question that is not scored is still asked and captured, but does not affect the score."
                    options={[
                      { label: 'Scored', value: true },
                      { label: 'Not scored', value: false }
                    ]}
                    dataCy="score-question-radio"
                  />
                  {!isQuestionScored && Object.values(optionPoints).some((points) => points > 0) && (
                    <Text
                      type="body3"
                      content="Saving will clear the points set for this question."
                      sx={{ color: palette.warning.text }}
                      dataCy="score-question-points-warning"
                    />
                  )}
                </ContainerItem>
              )}
              {isQuestionScored && (
                <ContainerItem padding="0 0 0.5rem 0" width="100%">
                  <Text type="body2" content="Points" />
                  <Text
                    type="body3"
                    content="Set the points each answer awards. Your organization's risk bands turn the final score into a rating."
                    sx={{ color: theme.palette.text.secondary }}
                  />
                  {scoredOptions.length === 0 ? (
                    <Text
                      type="body3"
                      content="Add options above to set their points."
                      sx={{ color: theme.palette.text.secondary }}
                    />
                  ) : (
                    scoredOptions.map((option) => (
                      <Container key={option} nowrap padding="4px 0" align="center">
                        <ContainerItem padding="0" width="60%">
                          <Text type="body3" content={option} />
                        </ContainerItem>
                        <ContainerItem padding="0" width="40%">
                          {/* NumericFormat to allow for only numbers (no decimals or whatever) without the form */}
                          <NumericFormat
                            value={String(optionPoints[option] ?? 0)}
                            onValueChange={(values) =>
                              setOptionPoints((current) => ({
                                ...current,
                                [option]: values.floatValue ?? 0
                              }))
                            }
                            decimalScale={0}
                            allowNegative={false}
                            inputMode="numeric"
                            aria-label={`Points for ${option}`}
                            aria-invalid={isOverMaxValue(option)}
                            data-cy={`field-option-points-${option}`}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: `1px solid ${
                                isOverMaxValue(option) ? palette.error.dark : theme.palette.divider
                              }`,
                              borderRadius: '4px',
                              fontFamily: 'inherit',
                              fontSize: '14px'
                            }}
                          />
                        </ContainerItem>
                      </Container>
                    ))
                  )}
                  {hasPointsOverMax && (
                    <Error
                      content={`* Points must be ${props.maxOptionPoints} or less.`}
                      dataCy="field-option-points-max-error"
                    />
                  )}
                </ContainerItem>
              )}
              {isQuestionScored && (
                <ContainerItem padding="0.5rem 0" width="100%">
                  <WeightInput
                    variant="form"
                    form={form}
                    name="weight"
                    label="Question Weight (%)"
                    descriptionText="How much this question counts within its section. Question weights should total 100% per section."
                    dataCy="field-weight"
                  />
                </ContainerItem>
              )}
              {props.optionsLocked.isTarget && (
                <>
                  <Container nowrap padding="0" align="center">
                    <ContainerItem>
                      <LockIcon />
                    </ContainerItem>
                    <ContainerItem padding="0">
                      <Text
                        type="body3"
                        content="The options are locked because smart rules depend on them. In order to modify these options, remove the smart rules targeting the following fields or sections: "
                      />
                    </ContainerItem>
                  </Container>
                  <Container padding="0 8px">
                    <ContainerItem padding="0">
                      <ul style={{ marginTop: 0 }}>
                        {props.optionsLocked.smartRuleSubjects.map((field) => {
                          return (
                            <li style={{ margin: 0 }}>
                              <Text type="body3" content={field.label} />
                            </li>
                          );
                        })}
                      </ul>
                    </ContainerItem>
                  </Container>
                </>
              )}
            </>
          )}

          {form.watch('type') !== CustomFieldType.DisplayContent && (
            <ContainerItem padding="0.5rem 0" width="100%">
              <InputCheckBox
                {...form}
                name="showHelpText"
                label="Show help text"
                dataCy="field-show-help-text-checkbox"
              />
            </ContainerItem>
          )}

          {form.watch('type') !== CustomFieldType.DisplayContent && form.watch('showHelpText') === true && (
            <ContainerItem padding="0.5rem 0" width="100%">
              <InputText
                {...form}
                name="helpText"
                label="Help Text"
                placeholder="Enter field help text or short description"
                dataCy="field-help-text"
              />
            </ContainerItem>
          )}

          {form.watch('type') !== CustomFieldType.DisplayContent &&
            form.watch('type') !== CustomFieldType.Hyperlink && (
              <ContainerItem padding="0.5rem 0" width="100%">
                <InputCheckBox
                  {...form}
                  name="required"
                  label="Required field"
                  dataCy="field-required-checkbox"
                />
              </ContainerItem>
            )}

          {form.watch('type') === CustomFieldType.DisplayContent && (
            <>
              <ContainerItem padding="0.5rem 0" width="100%">
                <InputText
                  name="label"
                  label="Heading"
                  dataCy="field-header"
                  placeholder="Enter heading text"
                  {...form}
                />
              </ContainerItem>
              <ContainerItem padding="0.5rem 0" width="100%">
                <InputTextArea
                  background="lightGray"
                  name="paragraph"
                  label="Paragraph"
                  dataCy="field-paragraph"
                  placeholder="Enter paragraph text"
                  minRows={3}
                  maxRows={3}
                  {...form}
                />
              </ContainerItem>
              <ContainerItem margin="-0.6rem 0 0.5rem 0" width="100%" padding="0">
                <FileContainer
                  newFilesPadding="12px 5px 12px 12px" // added this padding because, the existingFilees & newFiles are not aligned properly.
                  existingFilesLabel="Attachments"
                  uploadFilesLabel={existingFiles.length === 0 ? 'Attachments' : undefined}
                  fileBucket={environment.storageConfig.sharedDocument}
                  existingS3Files={existingFiles}
                  fullWidth
                  multiple
                  acceptedTypes="all"
                  onRemoveExistingFile={onRemoveExistingFile}
                  onNewFilesChange={(files: File[]) => {
                    setTempFiles([...files]);
                  }}
                  hardResetComponent={refreshFileComponent} // to reset the FileContainer when checked Create Another Field check box as data needs to be cleared
                  triggerRefresh={triggerRefresh}
                />
              </ContainerItem>
            </>
          )}

          {form.watch('type') === CustomFieldType.FileDownload && (
            <ContainerItem margin="0.5rem 0" width="100%" padding="0">
              <FileContainer
                newFilesPadding="12px 5px 12px 12px" // added this padding because, the existingFilees & newFiles are not aligned properly.
                existingFilesLabel="Attachments"
                uploadFilesLabel={existingFiles.length === 0 ? 'Attachments' : undefined}
                fileBucket={environment.storageConfig.sharedDocument}
                existingS3Files={existingFiles}
                fullWidth
                multiple
                acceptedTypes="all"
                onRemoveExistingFile={onRemoveExistingFile}
                onNewFilesChange={(files: File[]) => {
                  setTempFiles([...files]);
                }}
                hardResetComponent={refreshFileComponent} // to reset the FileContainer when checked Create Another Field check box as data needs to be cleared
                triggerRefresh={triggerRefresh}
              />
            </ContainerItem>
          )}

          <ContainerItem padding="0.5rem 0" width="100%">
            <InputSelect
              {...form}
              name="section"
              label="Section"
              options={props.sectionOptions?.filter((s) => s.label !== '') ?? []}
              additionalOnChange={(e) => props.onSectionSelect(e.target.value)}
              value={activeSection}
              emptyLabel="Select a section..."
              dataCy="field-section"
              rules={{
                required: { value: true, message: 'is required.' },
                validate: (data) => {
                  const value = data as string;

                  if (value === 'create-section') {
                    return false;
                  }

                  return true;
                }
              }}
            />
          </ContainerItem>

          {form.watch('type') === CustomFieldType.File && (
            <ContainerItem margin="0.2rem 0 0.5rem 0" width="100%" padding="0">
              <FileContainer
                newFilesPadding="12px 5px 12px 12px" // added this padding because, the existingFilees & newFiles are not aligned properly.
                existingFilesLabel="Attachments"
                uploadFilesLabel={existingFiles.length === 0 ? 'Attachments' : undefined}
                fileBucket={environment.storageConfig.sharedDocument}
                existingS3Files={existingFiles}
                fullWidth
                multiple
                acceptedTypes="all"
                onRemoveExistingFile={onRemoveExistingFile}
                onNewFilesChange={(files: File[]) => {
                  setTempFiles([...files]);
                }}
                hardResetComponent={refreshFileComponent} // to reset the FileContainer when checked Create Another Field check box as data needs to be cleared
                triggerRefresh={triggerRefresh}
              />
            </ContainerItem>
          )}

          {!props.field && !props.scored && (
            <ContainerItem padding="0.5rem 0" width="100%">
              <InputCheckBox
                {...form}
                name="addSmartRules"
                label="Add Smart Rules"
                dataCy="smart-rules-checkbox"
                readOnly={form.watch('createAnotherField') || availableSmartRuleFields.length === 0}
              />
            </ContainerItem>
          )}
          {!props.field && !props.scored && availableSmartRuleFields.length === 0 && (
            <ContainerItem padding="0.5rem 0" width="100%">
              <Text
                type="body3"
                sx={{ color: theme.palette.text.secondary }}
                content="To add Smart Rules, first create a field with the Radio Buttons or Select Lists field type to base a rule upon."
              />
            </ContainerItem>
          )}
        </Container>
      </Form>
    </Dialog>
  );
};
